import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '@/store';
import {
  selectOrganizationUsers,
  selectOrgLoading,
  selectOrgLoadingAny,
  selectInternalSeats,
  selectExternalSeats,
} from '@/store/selectors/organization';
import { fetchOrgUsers } from '@/store/thunks/organization/fetchOrgUsers';
import { fetchOrganizationDashboard } from '@/store/thunks/organization/fetchOrganizationDashboard';
import { createUserThunk } from '@/store/thunks/userForm/createUserThunk';
import { updateUserThunk } from '@/store/thunks/userForm/updateUserThunk';
import { fetchInvitationLinkThunk } from '@/store/thunks/userForm/fetchInvitationLinkThunk';
import { useModal } from '@/hooks/useModal';
import { useUserDialogs } from '@/hooks/useUserDialogs';
import { getSessionToken } from '@/utils/sessionStorage';
import { decodeJWT } from '@/utils/jwtDecoder';
import type { SeatBucket } from '@/types/organization.types';
import type { PortalUserValues, Scope, SeatAvailability, UserRow } from '@/types/users.types';
import { normalizeOrgUsersStatus, OrgUserStatus } from '@/types/status.types';

type TokenPayload = { organizationId?: string; sub?: string } & Record<string, unknown>;

const MIN_SEARCH_LENGTH = 0;

export function useUsersPage() {
  const dispatch = useAppDispatch();

  // global store
  const users = useSelector(selectOrganizationUsers);
  const loadingMap = useSelector(selectOrgLoading);
  const loadingAny = useSelector(selectOrgLoadingAny);
  const internalSeats = useSelector(selectInternalSeats);
  const externalSeats = useSelector(selectExternalSeats);

  // Seat counts start at 0 (available too), so before a successful load every
  // type would read as "full". Gate the limit on a confirmed load and fail open.
  const [seatsReady, setSeatsReady] = useState<boolean>(false);

  // Org id from the session JWT — needed for the dashboard load (which carries seats).
  const organizationId = useMemo<string>(() => {
    try {
      const token = getSessionToken() ?? '';
      if (token.length === 0) return '';
      const { payload } = decodeJWT<TokenPayload>(token);
      const fromOrg = typeof payload.organizationId === 'string' ? payload.organizationId : '';
      if (fromOrg.length > 0) return fromOrg;
      return typeof payload.sub === 'string' ? payload.sub : '';
    } catch {
      return '';
    }
  }, []);

  // UI hooks (Redux-backed)
  const { modal, isType, close: closeModal } = useModal();
  const {
    openCreateUser,
    openEditUser,
    openUserCreated,
    confirmToggleActive,
    confirmForceLogout,
    confirmForceLoginReset,
    confirmDelete,
  } = useUserDialogs();

  // local view state (filters/search)
  const [search, setSearch] = useState<string>('');
  const [scope, setScope] = useState<Scope>('all');
  const [deptValues, setDeptValues] = useState<string[]>([]);
  const [statusValues, setStatusValues] = useState<string[]>([]);

  // modal flags
  const isCreate = isType('createUser');
  const isEdit = isType('editUser');
  const isUserCreated = isType('userCreated');
  const editingUser: UserRow | undefined =
    isEdit && modal !== null && 'user' in modal ? modal.user : undefined;

  // Data load on mount. Seats are embedded in the org dashboard payload (the
  // standalone seats endpoint does not exist), so the dashboard load doubles as
  // the seat-count source. Fall back to a plain users fetch if we have no org id.
  useEffect(() => {
    if (organizationId.length > 0) {
      void dispatch(fetchOrganizationDashboard({ id: organizationId }))
        .unwrap()
        .then(() => {
          setSeatsReady(true);
        })
        .catch(() => {
          /* leave seats unenforced (fail open) if the load fails */
        });
    } else {
      void dispatch(fetchOrgUsers());
    }
  }, [dispatch, organizationId]);

  // Seat-limit per user type: active + new >= available.
  const isSeatLimitReached = useCallback(
    (bucket: SeatBucket): boolean => bucket.active + bucket.new >= bucket.available,
    []
  );

  const seatFull: SeatAvailability = useMemo(
    () => ({
      Internal: isSeatLimitReached(internalSeats),
      External: isSeatLimitReached(externalSeats),
    }),
    [isSeatLimitReached, internalSeats, externalSeats]
  );

  // Activating only consumes a seat for Inactive users (Active->Inactive frees one,
  // New->Active is seat-neutral since both count toward active + new). Hide the
  // "Make Active" option only when no free seat exists. Fail open until seats load.
  const canActivateUser = useCallback(
    (row: UserRow): boolean => {
      if (row.status !== OrgUserStatus.Inactive) return true;
      if (!seatsReady) return true;
      return !seatFull[row.userType];
    },
    [seatsReady, seatFull]
  );

  // normalize store to UserRow
  const normalizedRows: UserRow[] = useMemo(() => {
    const asStatus = (value: unknown): UserRow['status'] =>
      normalizeOrgUsersStatus(value, OrgUserStatus.Inactive);

    const asUserType = (value: unknown): UserRow['userType'] =>
      value === 'Internal' || value === 'External' ? value : 'Internal';

    return users.map((user) => ({
      id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone,
      deptOrg: user.deptOrg,
      status: asStatus(user.status),
      userType: asUserType(user.userType),
    }));
  }, [users]);

  // select options from rows
  const { deptOptions, statusOptions } = useMemo(() => {
    const deptSet = new Set<string>();
    const statusSet = new Set<string>();

    for (const row of normalizedRows) {
      if (row.deptOrg.length > MIN_SEARCH_LENGTH) deptSet.add(row.deptOrg);
      if (row.status.length > MIN_SEARCH_LENGTH) statusSet.add(row.status);
    }

    return {
      deptOptions: ['Dept / Org', ...Array.from(deptSet).sort()],
      statusOptions: ['Status', ...Array.from(statusSet).sort()],
    };
  }, [normalizedRows]);

  // filtered rows (view-only logic is fine to keep here)
  const filteredRows: UserRow[] = useMemo(() => {
    const query = search.trim().toLowerCase();

    return normalizedRows.filter((row) => {
      if (scope !== 'all' && row.userType.toLowerCase() !== scope) return false;
      if (deptValues.length > MIN_SEARCH_LENGTH && !deptValues.includes(row.deptOrg)) return false;
      if (statusValues.length > MIN_SEARCH_LENGTH && !statusValues.includes(row.status))
        return false;

      if (query.length > MIN_SEARCH_LENGTH) {
        const haystack = [row.name, row.email, row.phone, row.deptOrg, row.status]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [normalizedRows, scope, deptValues, statusValues, search]);

  // actions exposed to page (no dispatch in the page)
  const openCreate = useCallback(() => {
    openCreateUser();
  }, [openCreateUser]);
  const editRow = useCallback(
    (row: UserRow) => {
      openEditUser(row);
    },
    [openEditUser]
  );

  const toggleActive = useCallback(
    (row: UserRow) => {
      confirmToggleActive(row);
    },
    [confirmToggleActive]
  );
  const forceLogout = useCallback(
    (row: UserRow) => {
      confirmForceLogout(row);
    },
    [confirmForceLogout]
  );
  const forceLoginReset = useCallback(
    (row: UserRow) => {
      confirmForceLoginReset(row);
    },
    [confirmForceLoginReset]
  );
  const removeUser = useCallback(
    (row: UserRow) => {
      confirmDelete(row);
    },
    [confirmDelete]
  );

  const submit = useCallback(
    async(values: PortalUserValues) => {
      try {
        if (isCreate) {
          const createdUser = await dispatch(createUserThunk(values)).unwrap();
          const { invitationLink, otp } = await dispatch(
            fetchInvitationLinkThunk({ userId: createdUser.userId })
          ).unwrap();
          openUserCreated(invitationLink, otp);
        } else if (isEdit && editingUser !== undefined) {
          // !== check produce error in build
          await dispatch(updateUserThunk({ id: editingUser.id, data: values })).unwrap();
          closeModal();
        }
        // Refresh users + seats together via the dashboard (seats live in it).
        if (organizationId.length > 0) {
          void dispatch(fetchOrganizationDashboard({ id: organizationId }));
        } else {
          void dispatch(fetchOrgUsers());
        }
      } catch {
        // errors surfaced via thunks/slices
      }
    },
    [dispatch, organizationId, isCreate, isEdit, editingUser, openUserCreated, closeModal]
  );

  return {
    // view data
    rows: filteredRows,
    loading: loadingAny || loadingMap.orgUsers,
    deptOptions,
    statusOptions,

    // modal state
    modal,
    isCreate,
    isEdit,
    isUserCreated,
    editingUser,
    closeModal,

    // seat limits
    seatFull,
    seatsReady,

    // header/filter controls
    search,
    setSearch,
    scope,
    setScope,
    deptValues,
    setDeptValues,
    statusValues,
    setStatusValues,

    // actions
    openCreate,
    editRow,
    toggleActive,
    canActivateUser,
    forceLogout,
    forceLoginReset,
    removeUser,
    submit,
  };
}
